<template>
  <div class="content">
    <div v-if="currentPromo">
      <div class="promo-header">
        <div class="flex-row flex-center text-center">
          <div class="flex-column flex-center">
            <button
              v-if="promos.list.length > 1"
              @click="prevPromo"
              class="pi pi-chevron-left"
            ></button>
          </div>

          <div class="banner-image flex-center">
            <div v-if="currentPromo.banner_image">
              <div
                v-if="
                  currentPromo.banner_image && currentPromo.mobile_banner_image
                "
              >
                <img
                  :src="currentPromo.banner_image"
                  class="cover desktop-image"
                  :alt="currentPromo.title"
                />
                <img
                  :src="currentPromo.mobile_banner_image"
                  class="cover mobile-image"
                  :alt="currentPromo.title"
                />
              </div>

              <img
                :src="currentPromo.banner_image"
                class="cover"
                :alt="currentPromo.title"
              />
            </div>
            <img
              v-else
              src="@/assets/img/promotions/spotlight.jpeg"
              class="cover"
              alt="Promotions Spotlight image."
            />
          </div>
          <div class="flex-column flex-center">
            <button
              v-if="promos.list.length > 1"
              @click="nextPromo"
              class="pi pi-chevron-right"
            ></button>
          </div>
        </div>
        <div class="flex-row flex-center">
          <div v-if="promos.list.length > 1" class="dots">
            <button
              v-for="(promo, index) in promos.list"
              :key="promo.id"
              @click="currentPromoIndex = index"
            >
              <div
                class="pi pi-circle"
                :class="{ 'pi pi-circle-fill': index === currentPromoIndex }"
              ></div>
            </button>
          </div>
        </div>
      </div>

      <div class="title">
        <div v-if="currentPromo.title_image" class="title-image-container">
          <img :src="currentPromo.title_image" />
        </div>
        <h1 v-else>{{ currentPromo.title }}</h1>
      </div>

      <div class="promo-description">
        <span v-html="currentPromo.description_column_1"></span>
        <div
          v-if="currentPromo.promo_download_images.length > 0"
          class="downloadable-images"
        >
          <img
            v-for="image in currentPromo.promo_download_images"
            :src="image.image"
            alt="Downloadable image."
          />
          <button @click="downloadImages">
            <i class="pi pi-download"></i>
          </button>
        </div>
        <div class="terms-and-conditions">
          <span v-html="currentPromo.description_column_2"></span>
        </div>
      </div>
    </div>
  </div>
</template>

<route lang="json">
{ "name": "promotions" }
</route>

<script setup lang="ts">
import { usePromotionsStore } from "@/stores/promotions";
import { useHead } from "@unhead/vue";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

const route = useRoute();
const router = useRouter();
const promos = usePromotionsStore();
const currentPromoIndex = ref(0);

watch(currentPromoIndex, () => {
  router.replace({ query: { id: promos.list[currentPromoIndex.value].id } });
});

const currentPromo = computed(() => {
  if (promos.list.length > 0) {
    return promos.list[currentPromoIndex.value];
  }

  return null;
});

useHead({
  title: "Jewelry Blockbuster Collection",
  meta: [
    {
      name: "description",
      content:
        "Unlock Paparazzi Blockbuster jewelry packs with every order! Get limited edition pieces while supplies last.",
    },
  ],
  link: [
    {
      rel: "canonical",
      href: "https://paparazziaccessories.com/promotions",
    },
  ],
});

function prevPromo() {
  if (currentPromoIndex.value - 1 < 0) {
    currentPromoIndex.value = promos.list.length - 1;
  } else {
    currentPromoIndex.value -= 1;
  }
}
function nextPromo() {
  if (currentPromoIndex.value + 1 > promos.list.length - 1) {
    currentPromoIndex.value = 0;
  } else {
    currentPromoIndex.value += 1;
  }
}

async function downloadImages() {
  if (currentPromo.value) {
    currentPromo.value.promo_download_images.forEach(async (image: any) => {
      const fetchedImage = await fetch(image.image);
      const imageBlob = await fetchedImage.blob();
      const imageURL = URL.createObjectURL(imageBlob);

      const link = document.createElement("a");
      link.href = imageURL;
      link.download = image.image.split("/").at(-1) ?? "fashion-fix-image";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
  }
}

onMounted(async () => {
  await promos.sync();
  if (route.query.id) {
    const promoId = route.query.id as string;
    if (promoId) {
      const index = promos.list.findIndex((p) => p.id === parseInt(promoId));
      if (index > -1) {
        currentPromoIndex.value = index;
      }
    }
  }
});
</script>

<style scoped>
.content :deep(p) {
  color: var(--Black, #000);
  font-family: Indivisible;
  font-size: 14px !important;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-bottom: 18px;
}
.content :deep(p:last-child) {
  margin-bottom: 0;
}
.content :deep(p strong) {
  font-weight: 500;
}
.content :deep(ul li) {
  color: var(--Black, #000);
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  font-family: Indivisible;
}
.content :deep(h1) {
  color: var(--Paparazzi-Pink, #ec008c);
  text-align: center;
  font-family: Indivisible;
  font-size: 18px;
  font-style: normal;
  font-weight: 300;
  line-height: normal;
  text-transform: uppercase;
  margin: 0 0 24px 0;
}
.content :deep(h2) {
  color: var(--Paparazzi-Pink, #ec008c);
  text-align: center;
  /* Heading 2 */
  font-family: Indivisible;
  font-size: 24px;
  font-style: normal;
  font-weight: 300;
  line-height: normal;
  text-transform: uppercase;
}
.content :deep(h3) {
  color: var(--Black, #000);
  text-align: center;
  /* Heading 3 */
  font-family: Indivisible;
  font-size: 18px;
  font-style: normal;
  font-weight: 300;
  line-height: normal;
  text-transform: uppercase;
}
.downloadable-images {
  display: flex;
  gap: 24px;
  justify-content: center;
  flex-wrap: wrap;

  margin-bottom: 40px;

  img {
    height: 250px;
    width: 250px;
    object-fit: cover;
  }

  button {
    background-color: transparent;
    border: none;
  }
}
.terms-and-conditions {
  margin-top: 24px;
}
.terms-and-conditions:deep(p) {
  color: #757575;
  font-family: Indivisible;
  font-size: 12px !important;
  font-style: italic;
  font-weight: 400;
  line-height: normal;
}
.content {
  max-width: 740px;
}

.promo-header {
  padding-bottom: 32px;
  max-width: 628px;
  margin: 0 auto;
  button {
    background-color: var(--White, #fff);
    border: none;
    font-size: 12px;
    padding: 0;
  }
}
.banner-image {
  width: 100%;
  overflow: hidden;
  padding: 0 var(--XS, 16px);
  height: 280px;
  img.cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
    max-width: 548px;
  }
}

.promo-title {
  padding-bottom: var(--XMd, 40px);
  img {
    margin: 0 auto;
    max-width: 450px;
    display: block;
  }
}

.promo-description :deep(p) {
  margin-top: 0;
}
.promo-description .terms-and-conditions p {
  color: var(--Black, #000);
  font-family: Indivisible;
  font-size: 12px;
  font-style: italic;
  font-weight: 400;
  line-height: normal;
}
.promo-images {
  padding-bottom: var(--XMd, 40px);
}
.dots {
  padding-top: 8px;
  button {
    padding: 0;
  }
  .pi.pi-circle {
    font-size: 4px;
    margin: 0 var(--3XS, 4px);
  }
  .pi.pi-circle-fill {
    color: var(--Paparazzi-Pink, #ec008c);
  }
}

.title {
  margin-bottom: 32px;

  .title-image-container {
    display: flex;
    justify-content: center;

    height: 120px;
    width: 100%;

    img {
      height: 100%;
    }
  }
}

@media (max-width: 900px) {
  .downloadable-images {
    flex-direction: column;
    align-items: center;
  }
  .desktop-image {
    display: none;
  }
}

@media (min-width: 900px) {
  .downloadable-images {
    flex-direction: row;
    align-items: flex-end;
  }
  .mobile-image {
    display: none;
  }
}

@media (max-width: 800px) {
  .promo-title {
    img {
      max-width: 400px;
    }
  }
  @media (max-width: 500px) {
    .promo-title {
      img {
        max-width: 100%;
      }
    }
  }
}
</style>
